import { Card, Checkbox } from '@/components_deprecated'
import {
  Combobox,
  UserCombobox,
  useHideBasedOnLeaderGroup,
} from '@/components/combobox'
import { MonthYearPicker } from '@/components/month-year-picker'
import { PageType } from '@/modules/common/consts'
import { Field } from '@/modules/companies'
import { getNodes } from '@/utils/graphql'
import { usePermissions, useUser, useLeaderGroups } from '@/utils/hooks'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { UserWorkPeriodsWarningCard } from './UserWorkPeriodsWarningCard'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import {
  WORK_PERIODS,
  SALARY_MONTH_SUMMARY,
  REMAINING_WORK_PERIOD_RESOLVING,
} from './queries'
import { WorkPeriodApproveCards } from './WorkPeriodApproveCards'
import { WorkPeriodApproveTable } from './WorkPeriodApproveTable'
import { H1 } from '@/components'
import { getApprovalRows } from './approval-rows'
import { WORK_PERIOD_FILTER_STORAGE } from './work-period-filter-storage'
import { SalaryMonthSummaryCards } from './salary-month-summary-cards'
import { aggregateMonthSummary } from './utils/aggregate-month-summary'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const InfoContentWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: grid;
    grid-template-columns: 1fr auto auto auto auto;
    gap: 1rem;
    padding: 1rem;

    & + section {
      grid-template-columns: auto;
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      grid-template-columns: 1fr;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
      }
    }
  }
`

export const WorkPeriodApproveView: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    chooseGroup: 'common.choose-group',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',
    showPlannedWork: 'salary.show-planned-work',

    leaderGroups: 'companies.leader-groups',
  })

  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )
  const { InfoModal, infoContent, InfoButton } = useInfoModal(
    PageType.CONFIRM_HOURS
  )

  const leaderGroups = useLeaderGroups()
  const { hasAccess, myLeaderGroups } = leaderGroups
  const { driverApprove, managerApprove } = useSalaryModuleConfiguration()
  const hasApprovalFlow = driverApprove || managerApprove

  const form = useForm<{
    user: string | null
    month: Date
    viewMode: 'calendar' | 'list'
    showResolved: boolean
    showPlannedWork: boolean
    leaderGroup: string | null
    leaderGroupsUsers: string[]
    sumsCollapsed: boolean
    onlyApprovedSums: boolean
  }>({
    // Field union shared with WorkPeriodOverview so user + month cache together.
    values: {
      user: null,
      month: new Date(),
      viewMode: 'calendar',
      showResolved: false,
      showPlannedWork: true,
      leaderGroup: null,
      leaderGroupsUsers: [],
      sumsCollapsed: false,
      onlyApprovedSums: false,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: WORK_PERIOD_FILTER_STORAGE,
        transformValues: {
          user: val => {
            return val && canSeeOthers ? String(val) : null
          },
          month: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          viewMode: val =>
            val === 'list' || val === 'calendar' ? val : undefined,
          sumsCollapsed: val => (typeof val === 'boolean' ? val : undefined),
          onlyApprovedSums: val => (typeof val === 'boolean' ? val : undefined),
        },
      },
    },
  })

  const hideUserItem = useHideBasedOnLeaderGroup(
    form.formValues.leaderGroup ?? '',
    leaderGroups
  )

  const datetimeStart = startOfMonth(form.formValues.month)
  const datetimeEnd = endOfMonth(form.formValues.month)

  const effectiveUser = form.formValues.user ?? (canSeeOthers ? null : me.id)
  const queryVariables = {
    user: effectiveUser,
    datetimeStart: formatISO(datetimeStart),
    datetimeEnd: formatISO(datetimeEnd),
  }

  const { data, loading, refetch } = useQuery(WORK_PERIODS, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  })

  // `cache-and-network` lets a revisited month/user render instantly from
  // cache while a background refetch updates with fresh values. When the
  // cache misses (first visit) `data` is undefined → the summary card
  // renders its built-in skeleton.
  const { data: monthSummary, refetch: refetchMonthSummary } = useQuery(
    SALARY_MONTH_SUMMARY,
    {
      skip: !effectiveUser,
      fetchPolicy: 'cache-and-network',
      variables: {
        user: effectiveUser ?? '',
        datetimeStart: queryVariables.datetimeStart,
        datetimeEnd: queryVariables.datetimeEnd,
      },
    }
  )

  // The "N users need approval" banner is company-wide (no user filter) and
  // far cheaper than the work-period list, so it runs as its own query and
  // paints independently instead of blocking on the heavy list. Only fetched
  // when the viewer can see other users (the only case the banner renders).
  const { data: remainingData, refetch: refetchRemaining } = useQuery(
    REMAINING_WORK_PERIOD_RESOLVING,
    {
      skip: !canSeeOthers,
      fetchPolicy: 'cache-and-network',
      variables: {
        datetimeStart: queryVariables.datetimeStart,
        datetimeEnd: queryVariables.datetimeEnd,
      },
    }
  )

  // Mutations from the table/cards call `initRefetch` after editing work
  // periods, diets, leader groups, transfer-to-flex, etc. All queries
  // must refresh, otherwise the summary cards / banner keep showing stale
  // totals.
  const refetchAll = () => {
    void refetch()
    if (effectiveUser) void refetchMonthSummary()
    if (canSeeOthers) void refetchRemaining()
  }

  const workPeriods = getNodes(data?.allWorkPeriods)
  const calendarItems = getNodes(data?.allCalendarItems)
  const plannedWorkFilterEnabled = form.formValues.showPlannedWork !== false
  // Show the "Show planned work" checkbox and the planned calendar
  // items as rows if any work period uses planner overtime, or when
  // only planned shifts (no work logged yet) are returned.
  const anyWorkPeriodUsesPlannerForOvertime = workPeriods.some(
    wp => wp.salarySetting?.usePlannerForOvertime
  )
  const plannerOvertimeRelevant =
    anyWorkPeriodUsesPlannerForOvertime || calendarItems.length > 0
  const showPlannedWork = plannerOvertimeRelevant && plannedWorkFilterEnabled
  const approvalRows = getApprovalRows(
    workPeriods,
    showPlannedWork ? calendarItems : []
  )
  const salaryCalculationApprovalRows = approvalRows.filter(
    row => row.user.includeInSalaryCalculation
  )
  const selectedLeaderGroupUsers = new Set(form.formValues.leaderGroupsUsers)
  const selectedLeaderGroup = form.formValues.leaderGroup

  const approvalRowsInScope = salaryCalculationApprovalRows.filter(row => {
    if (!selectedLeaderGroup) return true
    return (
      selectedLeaderGroupUsers.has(row.user.id) ||
      row.workPeriod?.leaderGroup?.id === selectedLeaderGroup
    )
  })

  const displayApprovalRows = approvalRowsInScope.filter(row => {
    if (form.formValues.showResolved) return true
    return row.type === 'calendarItem' || !row.workPeriod.approvedByManager
  })

  const userTasks = (remainingData?.remainingWorkPeriodResolving ?? []).filter(
    task => task.user.includeInSalaryCalculation
  )

  const remainingWorkPeriodResolvingCount = userTasks.length ?? 0

  const leaderGroupUserIds = (groupId: string | null) =>
    myLeaderGroups.find(g => g.id === groupId)?.users.map(user => user.id) ?? []

  // The user select only offers members of the selected leader group, so a
  // user picked before the group change can fall outside it — drop the
  // selection instead of leaving a value the select can no longer show.
  const changeLeaderGroup = (value: string | null) => {
    const groupUsers = leaderGroupUserIds(value)
    const selectedUser = form.formValues.user
    const keepUser =
      !value || !selectedUser || groupUsers.includes(selectedUser)

    form.updateForm({
      leaderGroup: value,
      leaderGroupsUsers: groupUsers,
      user: keepUser ? selectedUser : null,
    })
  }

  // The approval banner lists everyone in the company, so picking a user from
  // it can point outside the selected group. Clear the group filter instead of
  // selecting a user the select hides and the rows filter away.
  const selectUser = (userId: string) => {
    const selectedGroup = form.formValues.leaderGroup
    const outsideGroup =
      !!selectedGroup && !leaderGroupUserIds(selectedGroup).includes(userId)

    form.updateForm({
      user: userId,
      ...(outsideGroup ? { leaderGroup: null, leaderGroupsUsers: [] } : {}),
    })
  }

  return (
    <Wrapper>
      <InfoContentWrapper>
        <H1>{translations.confirmHours}</H1>
        {infoContent && <InfoButton />}
      </InfoContentWrapper>

      {canSeeOthers && userTasks && remainingWorkPeriodResolvingCount > 0 && (
        <div className="mb-4">
          <UserWorkPeriodsWarningCard
            users={userTasks}
            title={
              remainingWorkPeriodResolvingCount === 1
                ? translations.oneUserNeedsApproval
                : translations.nUsersNeedApproval({
                    n: remainingWorkPeriodResolvingCount,
                  })
            }
            onSelectUser={selectUser}
          />
        </div>
      )}
      <Controls>
        <section>
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserCombobox
                mode="single"
                value={form.formValues.user ?? ''}
                onChange={user => form.updateForm({ user: user || null })}
                filterSalaryResponsible={false}
                filterLeaderGroup
                filterSalaryCalculation
                hideItem={hideUserItem}
                clearable
                className="h-[3.875rem] min-w-[260px]"
              />
            </Field>
          )}
          {hasAccess && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <Combobox
                mode="single"
                value={form.formValues.leaderGroup ?? ''}
                items={
                  myLeaderGroups?.map(group => ({
                    value: group.id,
                    label: group.name,
                  })) ?? []
                }
                clearable
                placeholder={translations.chooseGroup}
                onChange={value => changeLeaderGroup(value || null)}
                className="h-[3.875rem] min-w-[220px]"
              />
            </Field>
          )}

          <Field>
            <label>{translations.showResolved}</label>
            <Checkbox
              checked={form.formValues.showResolved}
              onChange={checked => form.updateForm({ showResolved: checked })}
              boxSize="3.875rem"
              padding="2rem"
              boxBorderRadius="8px"
            />
          </Field>
          {plannerOvertimeRelevant && (
            <Field>
              <label>{translations.showPlannedWork}</label>
              <Checkbox
                checked={plannedWorkFilterEnabled}
                onChange={checked =>
                  form.updateForm({ showPlannedWork: checked })
                }
                boxSize="3.875rem"
                padding="2rem"
                boxBorderRadius="8px"
              />
            </Field>
          )}
        </section>
        <section>
          <Field>
            <label>{translations.month}</label>
            <MonthYearPicker
              value={form.formValues.month}
              onChange={date => form.updateForm({ month: startOfMonth(date) })}
              className="h-[3.875rem] min-w-[240px]"
            />
          </Field>
        </section>
      </Controls>

      {effectiveUser && (
        <SalaryMonthSummaryCards
          month={datetimeStart}
          rangeStart={datetimeStart}
          rangeEnd={datetimeEnd}
          summary={aggregateMonthSummary(
            monthSummary?.monthReport,
            datetimeStart,
            datetimeEnd,
            hasApprovalFlow && form.formValues.onlyApprovedSums
              ? { driverApprove, managerApprove }
              : undefined
          )}
          sumScope={
            hasApprovalFlow
              ? form.formValues.onlyApprovedSums
                ? 'approved'
                : 'all'
              : undefined
          }
          onSumScopeChange={scope =>
            form.updateForm({ onlyApprovedSums: scope === 'approved' })
          }
          loading={!monthSummary?.monthReport}
          collapsed={form.formValues.sumsCollapsed}
          onToggleCollapsed={() =>
            form.updateForm({
              sumsCollapsed: !form.formValues.sumsCollapsed,
            })
          }
        />
      )}

      {isMobileOnly ? (
        <WorkPeriodApproveCards
          approvalRows={displayApprovalRows}
          leaderGroups={myLeaderGroups}
          singleUserSelected={!!form.formValues.user}
          loading={loading}
          initRefetch={refetchAll}
        />
      ) : (
        <WorkPeriodApproveTable
          approvalRows={displayApprovalRows}
          leaderGroups={myLeaderGroups}
          showUserColumn={canSeeOthers}
          singleUserSelected={!!form.formValues.user}
          loading={loading}
          initRefetch={refetchAll}
        />
      )}
      {infoContent && <InfoModal />}
    </Wrapper>
  )
}
